


import type React from "react";

import { useEffect, useRef, useState } from "react";
import TextField from "@mui/material/TextField";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormLabel from "@mui/material/FormLabel";
import { Typography } from "@mui/material";
import ReactSelect from "react-select";
import PersonIcon from "@mui/icons-material/Person";
import { axiosInstance } from "../utility/apiInstance";

interface OptionType {
  id: number;
  label: string;
}

interface Fieldtype {
  id: number;
  f_name: string;
  f_type: string;
  default_value: string;
  is_required: number;
  options: OptionType[];
}

interface FieldData {
  id: string;
  autocompleteValue: OptionType | null;
  textValue: string | string[];
  value?: string | string[];
  isUnique?: boolean;
  prefilled?: boolean;   // true = was set from panel dropdown, lock the input
}

interface AddcustomfieldsFormProps {
  setmodalPopup: any;
  fields: FieldData[];
  setFields: React.Dispatch<React.SetStateAction<FieldData[]>>;
  errors: Record<string, { autocomplete?: boolean; text?: boolean }>;
  setErrors: React.Dispatch<React.SetStateAction<any>>;
  setDataArray: React.Dispatch<React.SetStateAction<any[]>>;
  simpleOptions: OptionType[];
  Fieldstype: any;
  fieldErrors: Record<string, string>;
  setFieldErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  subscriptionFormData: any;
  setSubscriptionFormData: React.Dispatch<React.SetStateAction<any>>;
  entryCustomerName?: any;
  onCustomerNameChange?: (option: any) => void;
}

type CustomFieldValidationError = {
  [key: string]: string;
};

const AddcustomfieldsForm: React.FC<AddcustomfieldsFormProps> = ({
  setmodalPopup,
  fields,
  setFields,
  errors,
  setErrors,
  setDataArray,
  simpleOptions,
  Fieldstype,
  fieldErrors,
  setFieldErrors,
  subscriptionFormData,
  setSubscriptionFormData,
  entryCustomerName,
  onCustomerNameChange,
}) => {
  const [fieldscase, setfieldscase] = useState<Fieldtype[]>([]);
  const [customFieldValidationError, setCustomFieldValidationError] =
    useState<CustomFieldValidationError>({});

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchOrganizations = async (query: string) => {
    try {
      const { data } = await axiosInstance.get(
        `/api/v1/master-contact-organization?search=${query}`
      );
      setSubscriptionFormData((prev: any) => ({
        ...prev,
        customersList: data?.data,
      }));
    } catch (err) {
      setSubscriptionFormData((prev: any) => ({
        ...prev,
        customersList: [],
      }));
    }
  };

  const handleCustomerInputChange = (inputValue: string) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      fetchOrganizations(inputValue);
    }, 400);
  };

  useEffect(() => {
    fetchOrganizations("");
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const setFieldValue = (fieldId: string, nextVal: string | string[]) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
    setFields((prev) =>
      prev.map((f) => (f.id === fieldId ? { ...f, value: nextVal } : f))
    );
  };

  useEffect(() => {
    if (!fields?.length) return;
    const newFieldscase = fields
      .map((field: any) =>
        Fieldstype?.find((f: any) => f?.id === field?.autocompleteValue?.id)
      )
      .filter(Boolean) as Fieldtype[];
    setfieldscase(newFieldscase);
  }, [fields]);

  const handleTextChange = async (
    fieldRowId: string,
    fieldId: string,
    value: string
  ) => {
    setCustomFieldValidationError({});

    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[fieldRowId];
      return next;
    });

    setFields((prev) =>
      prev.map((f) =>
        f.id === fieldRowId ? { ...f, textValue: value, value } : f
      )
    );
    if (value.trim()) {
      setErrors((prev: any) => ({
        ...prev,
        [fieldRowId]: { ...(prev[fieldRowId] || {}), text: false },
      }));
    }
    try {
      const data = await axiosInstance.post(
        `/api/v1/file/verify-custom-fields`,
        {
          CustomField: fieldId,
          customFieldValue: value,
        }
      );
      if (data.data.length > 0) {
        setCustomFieldValidationError({ [fieldId]: data.data[0].message });
      }
    } catch (e: any) {
      const err = e?.response?.data?.error?.[0]?.reason || "";
      setCustomFieldValidationError({ [fieldId]: err });
    }
  };

  const renderAssociatedControl = (fieldRow: FieldData) => {
    const selected = Fieldstype.find(
      (f: any) => f?.id === fieldRow?.autocompleteValue?.id
    );

    if (!selected) return null;

    const v = fieldRow?.value;

    // ── Lock only the field that was prefilled from the panel dropdown ──
    const isLocked = !!(fieldRow as any).prefilled;

    if (isLocked) {
      return (
        <input
          type="text"
          value={(v as string) ?? ""}
          readOnly
          tabIndex={-1}
          style={{
            width: "100%",
            height: 40,
            padding: "0 10px",
            fontSize: 13,
            fontFamily: "inherit",
            color: "#555",
            background: "#f5f5f5",
            border: "1px solid #e0e0e0",
            borderRadius: 4,
            cursor: "not-allowed",
            outline: "none",
            boxSizing: "border-box",
          }}
        />
      );
    }

    switch (selected.f_type) {
      case "Textbox":
        return (
          <>
            <TextField
              value={(v as string) ?? ""}
              onChange={(e) => {
                const val = e.target.value;
                handleTextChange(fieldRow?.id, selected.f_name, val);
              }}
              size="small"
              fullWidth
            />
            <span className="text-red-500 text-sm">
              {customFieldValidationError[selected.f_name] ?? " "}
            </span>
          </>
        );

      case "Textarea":
        return (
          <FormControl fullWidth>
            <TextareaAutosize
              minRows={2}
              value={(v as string) ?? ""}
              onChange={(e) => {
                const val =
                  typeof fieldRow.value === "string"
                    ? fieldRow.value
                    : e.target.value;
                handleTextChange(fieldRow?.id, selected.f_name, val);
              }}
              style={{
                padding: 8,
                borderRadius: 4,
                borderColor: "#e5e7eb",
                width: "100%",
                boxSizing: "border-box",
                resize: "none",
              }}
            />
            {errors?.[fieldRow.id]?.text && (
              <p className="text-red-500 text-sm">Field is empty</p>
            )}
          </FormControl>
        );

      case "select":
        return (
          <FormControl fullWidth size="small">
            <Select
              labelId={`select-${fieldRow.id}-label`}
              value={(v as string) ?? ""}
              onChange={(e) =>
                setFieldValue(fieldRow.id, e.target.value as string)
              }
            >
              {(selected.options || []).map((opt: OptionType) => (
                <MenuItem key={opt.id} value={String(opt.label)}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case "Radiobutton":
        return (
          <FormControl>
            <RadioGroup
              name={`radio-${fieldRow.id}`}
              value={(v as string) ?? ""}
              onChange={(e) => setFieldValue(fieldRow.id, e.target.value)}
              row
            >
              {(selected.options || []).map((opt: OptionType) => (
                <FormControlLabel
                  key={opt.id}
                  value={String(opt.id)}
                  control={<Radio size="small" />}
                  label={<span style={{ fontSize: 12 }}>{opt.label}</span>}
                />
              ))}
            </RadioGroup>
          </FormControl>
        );

      case "Checkbox": {
        const current = Array.isArray(v) ? (v as string[]) : [];
        const toggle = (idStr: string) => {
          const next = current.includes(idStr)
            ? current.filter((x) => x !== idStr)
            : [...current, idStr];
          setFieldValue(fieldRow.id, next);
        };
        return (
          <FormControl component="fieldset">
            <FormGroup row>
              {(selected.options || []).map((opt: OptionType) => {
                const idStr = String(opt.id);
                const checked = current.includes(idStr);
                return (
                  <FormControlLabel
                    key={opt.id}
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={() => toggle(idStr)}
                        size="small"
                      />
                    }
                    label={<span style={{ fontSize: 12 }}>{opt.label}</span>}
                  />
                );
              })}
            </FormGroup>
          </FormControl>
        );
      }

      case "Date": {
        return (
          <input
            type="date"
            value={(v as string) ?? ""}
            onChange={(e) => {
              const val = e.target.value;
              handleTextChange(fieldRow?.id, selected.f_name, val);
            }}
            style={{
              height: 40,
              width: "100%",
              padding: "0 14px",
              fontSize: 14,
              fontFamily: "inherit",
              color: "rgba(0,0,0,0.87)",
              border: "1px solid rgba(0,0,0,0.23)",
              borderRadius: 4,
              outline: "none",
              boxSizing: "border-box",
              background: "#fff",
              cursor: "pointer",
            }}
            onFocus={(e) => {
              e.target.style.border = "2px solid #1976d2";
              e.target.style.padding = "0 13px";
            }}
            onBlur={(e) => {
              e.target.style.border = "1px solid rgba(0,0,0,0.23)";
              e.target.style.padding = "0 14px";
            }}
            onMouseEnter={(e) => {
              (e.target as HTMLInputElement).style.border =
                "1px solid rgba(0,0,0,0.87)";
            }}
            onMouseLeave={(e) => {
              if (document.activeElement !== e.target) {
                (e.target as HTMLInputElement).style.border =
                  "1px solid rgba(0,0,0,0.23)";
              }
            }}
          />
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="modalInnerField acf-root">

      {/* ── Customer Name — per accordion entry ── */}
      {onCustomerNameChange && (
        <div className="acf-customer-row">
          <div className="acf-customer-icon-wrap">
            <PersonIcon style={{ fontSize: 15, color: "#185FA5" }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              style={{ fontSize: 12, fontWeight: 500, marginBottom: 4, color: "#555" }}
            >
              Customer Name
            </Typography>
            <ReactSelect
              isClearable
              name="customerName"
              options={subscriptionFormData?.customersList?.map((item: any) => ({
                label: item?.name,
                value: item?._id,
                item,
              }))}
              placeholder="Select customer"
              className="basic-multi-select"
              classNamePrefix="select"
              value={entryCustomerName || null}
              onInputChange={handleCustomerInputChange}
              onChange={(option: any) => onCustomerNameChange(option)}
              filterOption={() => true}
              styles={{
                control: (base) => ({
                  ...base,
                  backgroundColor: "#fff",
                  border: "0.5px solid rgba(0,0,0,0.15)",
                  borderRadius: 6,
                  boxShadow: "none",
                  minHeight: 32,
                  "&:hover": { borderColor: "rgba(0,0,0,0.3)" },
                }),
                placeholder: (base) => ({ ...base, color: "#aaa", fontSize: 12 }),
                singleValue: (base) => ({ ...base, fontSize: 12 }),
                input: (base) => ({ ...base, fontSize: 12 }),
                indicatorSeparator: () => ({ display: "none" }),
              }}
            />
          </div>
        </div>
      )}

      {/* ── Custom Fields — ONE horizontal row ── */}
      <div className="modalInnerFieldBody">
        {fields.length === 0 ? (
          <div className="acf-empty">
            <span style={{ fontSize: 28 }}>📋</span>
            <p style={{ fontSize: 13, fontWeight: 500, color: "#888", margin: 0 }}>
              No custom fields available
            </p>
          </div>
        ) : (
          <div className="acf-fields-row">
            {fields.map((option: any) => (
              <div className="acf-field-col" key={option.id}>

                {/* Label */}
                <label className="acf-field-label">
                  {option.label}
                  {option.required && (
                    <span style={{ color: "#e53e3e", marginLeft: 2 }}>*</span>
                  )}
                  {option.isUnique && (
                    <span className="acf-unique-badge">
                      <svg
                        width="9"
                        height="9"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 2C6.477 2 2 6.477 2 12" />
                        <path d="M12 6a6 6 0 0 1 6 6" />
                        <path d="M12 10a2 2 0 0 1 2 2" />
                        <path d="M7 12a5 5 0 0 1 5-5" />
                        <path d="M12 14v8" />
                      </svg>
                      Unique
                    </span>
                  )}
                </label>

                {/* Control */}
                <div className="acf-field-control">
                  {renderAssociatedControl(option)}
                </div>

                {/* Per-field error */}
                {fieldErrors[option.id] && (
                  <p className="acf-field-error">{fieldErrors[option.id]}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
};

export default AddcustomfieldsForm;

export function buildPayloadFromFields(fields: FieldData[]) {
  return fields
    .filter((f) => f.autocompleteValue)
    .map((f) => {
      const key = f.autocompleteValue!.id;
      const value = f.value !== undefined ? f.value : f.textValue;
      return { key, value };
    });
}